import { Realtime, type TokenRequest } from 'ably';
import { ABLY_HOST_OPTIONS } from 'common/constants/ably';
import { logger } from 'common/services';
import EventEmitter from 'events';
import { updateKeycloakToken } from 'modules/keycloak';
import { keycloakService } from 'modules/keycloak/services';

/**
 * Socket Events
 */
export const SocketEvents = {
  connected: 'connected',
  disconnected: 'disconnected',
  reconnected: 'reconnected',
  message: 'message',
  error: 'error',
} as const;

/**
 * Socket Configuration
 */
export interface OpenNatterSocketConfig {
  apiBaseUrl: string;
  openNatterId: string;
  userId: string;
}

/**
 * Open Natter Ably Realtime Client
 * Handles dual-channel Ably pub/sub communication for Open Natter events
 */
export class OpenNatterSocket extends EventEmitter {
  private readonly client: Realtime;
  private isInitialized = false;

  constructor(private readonly config: OpenNatterSocketConfig) {
    super();
    const { apiBaseUrl, openNatterId, userId } = config;

    logger.debug('[OpenNatterSocket] Initializing Ably socket', { apiBaseUrl, openNatterId, userId });

    this.client = new Realtime({
      ...ABLY_HOST_OPTIONS,
      authCallback: async (_, callback) => {
        try {
          await updateKeycloakToken(keycloakService);
          const response = await fetch(`${apiBaseUrl}/open-natters/${openNatterId}/ably-token`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${keycloakService.token}` },
          });
          if (!response.ok) {
            logger.error('[OpenNatterSocket] Ably token fetch failed', { status: response.status });
            // ably's authCallback error param is `string | ErrorInfo | null`, not an Error.
            callback(`Ably token request failed: ${response.status}`, null);
            return;
          }
          // The endpoint returns a signed Ably TokenRequest JSON.
          const tokenRequest = (await response.json()) as TokenRequest;
          callback(null, tokenRequest);
        } catch (error) {
          logger.error('[OpenNatterSocket] Error in Ably authCallback', { error });
          callback(error instanceof Error ? error.message : String(error), null);
        }
      },
      clientId: userId,
      autoConnect: false,
    });

    this.client.connection.on('connected', () => {
      if (!this.isInitialized) {
        logger.info('[OpenNatterSocket] Connected');
        this.isInitialized = true;
        this.emit(SocketEvents.connected);
      } else {
        logger.info('[OpenNatterSocket] Reconnected');
        this.emit(SocketEvents.reconnected);
      }
    });

    this.client.connection.on('disconnected', () => {
      logger.info('[OpenNatterSocket] Disconnected');
      this.emit(SocketEvents.disconnected);
    });

    this.client.connection.on('suspended', () => {
      // `suspended` is NOT terminal — Ably keeps auto-retrying (~every 30s) and will fire `connected`
      // again on recovery (→ socketReconnected → re-join). So emit `disconnected` only; do NOT emit
      // an error (which would tell the user to refresh and destroy the client mid auto-recovery).
      logger.warn('[OpenNatterSocket] Suspended (auto-retrying)');
      this.emit(SocketEvents.disconnected);
    });

    this.client.connection.on('failed', (stateChange) => {
      logger.error('[OpenNatterSocket] Connection failed', { reason: stateChange?.reason?.message });
      this.emit(SocketEvents.disconnected);
      this.emit(SocketEvents.error, new Error(stateChange?.reason?.message ?? 'Ably connection failed'));
    });
  }

  /**
   * Connect to Ably
   */
  connect(): void {
    logger.debug('[OpenNatterSocket] Connecting');
    this.client.connect();
  }

  /**
   * Disconnect from Ably and clean up listeners
   */
  disconnect(): void {
    logger.debug('[OpenNatterSocket] Disconnecting');
    this.client.close();
    this.removeAllListeners();
  }

  /**
   * Subscribe to personal and broadcast Ably channels
   */
  subscribe(channels: { personal: string; broadcast: string }): void {
    logger.debug('[OpenNatterSocket] Subscribing to channels', {
      personal: channels.personal,
      broadcast: channels.broadcast,
    });

    const subscribeToChannel = (channelName: string, rewind: boolean) => {
      const channel = rewind
        ? this.client.channels.get(channelName, { params: { rewind: '1' } })
        : this.client.channels.get(channelName);

      // subscribe() attaches the channel; its promise rejects on attach failure (e.g. token
      // capability mismatch). A per-channel attach failure does NOT trigger the connection-level
      // failed/suspended events, so forward it to SocketEvents.error or the user is silently stuck.
      void channel
        .subscribe((msg) => {
          try {
            const payload = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
            this.emit(SocketEvents.message, { destination: channelName, payload });
          } catch (error) {
            logger.error('[OpenNatterSocket] Failed to parse Ably message', { error, channelName });
          }
        })
        .catch((error) => {
          logger.error('[OpenNatterSocket] Failed to attach to channel', { error, channelName });
          this.emit(SocketEvents.error, error instanceof Error ? error : new Error(String(error)));
        });

      logger.debug('[OpenNatterSocket] Subscribed to channel', { channelName, rewind });
      return channel;
    };

    subscribeToChannel(channels.personal, true);
    const broadcast = subscribeToChannel(channels.broadcast, false);

    // Presence is entered ONLY on the broadcast channel: the minted token grants
    // ["subscribe","presence"] there, but the personal channel is subscribe-only, so
    // entering presence on it would violate the token capability. Enter with a String
    // payload (userId) — Ably can't encode null presence data.
    void broadcast.presence
      .enter(this.config.userId)
      .catch((error) => logger.warn('[OpenNatterSocket] presence enter failed', { error }));
  }

  /**
   * Unsubscribe from personal and broadcast Ably channels
   */
  unsubscribe(channels: { personal: string; broadcast: string }): void {
    logger.debug('[OpenNatterSocket] Unsubscribing from channels', {
      personal: channels.personal,
      broadcast: channels.broadcast,
    });

    // Leave presence only on the broadcast channel — that's the only channel we entered presence on
    // (the personal channel is subscribe-only per the token capability, so leave() would be rejected).
    // Release the channels only AFTER the leave settles: release() forces a detach, and if that wins
    // the race it cancels the pending presence leave, leaving a stale member until TTL/connection close.
    void this.client.channels
      .get(channels.broadcast)
      .presence.leave()
      .catch((error) => logger.warn('[OpenNatterSocket] presence leave failed', { error }))
      .finally(() => {
        this.client.channels.release(channels.personal);
        this.client.channels.release(channels.broadcast);
      });

    [channels.personal, channels.broadcast].forEach((channelName) => {
      this.client.channels.get(channelName).unsubscribe();
      logger.debug('[OpenNatterSocket] Unsubscribed from channel', { channelName });
    });
  }

  /**
   * Check if the Ably connection is established
   */
  isConnected(): boolean {
    return this.client.connection.state === 'connected';
  }
}

// Socket instance is managed by OpenNatterFeature for proper lifecycle management
// Import from '../OpenNatterFeature' to get the socket instance
